<template>
    <IntegrationModal
        title="SendPulse"
        documentation-url="https://support.topol.io/en/articles/8694463-export-to-sendpulse"
        :invalid-validation="v$.$invalid"
        :loading="loading"
        :edit-mode="integration !== undefined"
        @close="$emit('close')"
        @create="createIntegration"
        @edit="updateIntegration"
    >
        <template #inputs>
            <div class="mb-6 w-full">
                <TextInput
                    v-model="v$.name.$model"
                    class="mt-6"
                    placeholder="Enter your preferred integration name"
                    label="Name"
                    :error="getError('name')"
                />

                <TextInput
                    v-model="v$.client_id.$model"
                    class="mt-6 w-full"
                    placeholder="Your SendPulse Client ID"
                    label="Client ID"
                    required
                    :error="getError('client_id')"
                />

                <TextInput
                    v-model="v$.client_secret.$model"
                    class="mt-6 w-full"
                    placeholder="Your SendPulse Client Secret"
                    label="Client Secret"
                    required
                    :error="getError('client_secret')"
                />
            </div>
        </template>
    </IntegrationModal>
</template>
<script lang="ts" setup>
import { route } from 'ziggy-js';
import { create } from '@composables/useNotifications';
import useVuelidate from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';
import IInputErrors from '@/types/Atomic/IInputErrors';
import { PropType } from 'vue';
import { ISendPulse, IIntegration } from '@/types/Integrations/IIntegration';
import useIntegrations from '../../composables/useIntegrations';

const props = defineProps({
    integration: {
        type: Object as PropType<IIntegration<ISendPulse>>,
        required: false,
        default: undefined,
    },
});
const emit = defineEmits(['close']);

const loading = ref(false);
const errors = ref<IInputErrors>({});
const { sendpulseIntegrationModalOpened, integrationCreated } =
    useIntegrations();
const state = reactive({
    name: props.integration?.name || '',
    client_id: '',
    client_secret: '',
});

const rules = {
    name: {
        maxLength: maxLength(255),
    },
    client_id: {
        required,
        maxLength: maxLength(255),
    },
    client_secret: {
        required,
        maxLength: maxLength(255),
    },
};

const v$ = useVuelidate(rules, state);

const getError = (field: string) => {
    return v$.value[field]?.$errors[0]?.$message || errors.value?.[field] || '';
};

const createIntegration = () => {
    v$.value.$touch();
    if (v$.value.$error) {
        return;
    } else {
        loading.value = true;
        router.post(
            route('integrations.sendpulse.store'),
            {
                name: state.name,
                client_id: state.client_id,
                client_secret: state.client_secret,
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: 'Integration added',
                        text: 'SendPulse integration has been successfully added.',
                        type: 'success',
                    });
                    integrationCreated('SendPulse');
                    sendpulseIntegrationModalOpened.value = false;
                },
                onError: (err) => {
                    errors.value = err;
                    create({
                        title: 'Integration not added',
                        text:
                            err.message ??
                            'There was an error adding SendPulse integration.',
                        type: 'error',
                    });
                },
                onFinish: () => {
                    loading.value = false;
                },
            }
        );
    }
};

const updateIntegration = () => {
    v$.value.$touch();
    if (v$.value.$error || !props.integration) {
        return;
    } else {
        loading.value = true;
        router.put(
            route('integrations.sendpulse.update', {
                integration: props.integration.id,
            }),
            {
                name: state.name,
                client_id: state.client_id,
                client_secret: state.client_secret,
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    emit('close');
                    create({
                        title: 'Integration updated',
                        type: 'success',
                    });
                },
                onError: (err) => {
                    errors.value = err;
                    create({
                        title: 'Integration not updated',
                        text:
                            err.message ??
                            'There was an error updating SendPulse integration.',
                        type: 'error',
                    });
                },
                onFinish: () => {
                    loading.value = false;
                },
            }
        );
    }
};
</script>

<style scoped></style>
